import { useState } from 'react';

import type { CompanyResponse, TerminationDocumentPolicy } from '../../api';
import {
  Form,
  FormError,
  Panel,
  SelectField,
  SubmitButton,
  useNotifications,
} from '../../components';
import { useSetCompanyTerminationDocumentPolicy } from '../queries';
import {
  ONBOARDING_COPY,
  TERMINATION_DOCUMENT_POLICY_HINTS,
  TERMINATION_DOCUMENT_POLICY_LABELS,
  TERMINATION_DOCUMENT_POLICY_OPTIONS,
} from './onboarding';

/**
 * What the review does about one company's terminations with no paperwork.
 *
 * The select starts on the policy the server last reported, which is also said
 * in words above it, so "what is in force?" never depends on the draft. A save
 * that restates it is answered on the screen rather than with a request that
 * would change nothing.
 */
export function TerminationDocumentPolicyPanel({ company }: { company: CompanyResponse }) {
  const setPolicy = useSetCompanyTerminationDocumentPolicy();
  const { notify } = useNotifications();
  const current = company.termination_document_policy;
  const [draft, setDraft] = useState<TerminationDocumentPolicy>(current);
  const [unchanged, setUnchanged] = useState(false);

  const save = () => {
    if (draft === current) {
      setUnchanged(true);
      return;
    }
    setUnchanged(false);
    setPolicy.mutate(
      { companyId: company.id, policy: draft },
      { onSuccess: () => notify('success', ONBOARDING_COPY.terminationPolicySaved) },
    );
  };

  return (
    <Panel
      title={ONBOARDING_COPY.terminationPolicyTitle}
      description={ONBOARDING_COPY.terminationPolicyDescription}
    >
      <p className="panel__actions" data-testid="termination-policy-in-force">
        {ONBOARDING_COPY.terminationPolicyCurrent}:{' '}
        <strong>{TERMINATION_DOCUMENT_POLICY_LABELS[current]}</strong>
      </p>
      <Form onSubmit={save} aria-label={ONBOARDING_COPY.terminationPolicyTitle}>
        <FormError error={setPolicy.error} />
        {unchanged && (
          <p className="field__error" role="alert">
            {ONBOARDING_COPY.terminationPolicyUnchanged}
          </p>
        )}
        <SelectField
          label={ONBOARDING_COPY.terminationPolicyLabel}
          value={draft}
          onChange={(chosen) => {
            if (chosen === '') {
              return;
            }
            setDraft(chosen);
            setUnchanged(false);
          }}
          options={TERMINATION_DOCUMENT_POLICY_OPTIONS}
          hint={TERMINATION_DOCUMENT_POLICY_HINTS[draft]}
          required
        />
        <SubmitButton
          pending={setPolicy.isPending}
          pendingLabel={ONBOARDING_COPY.terminationPolicySaving}
        >
          {ONBOARDING_COPY.terminationPolicySave}
        </SubmitButton>
      </Form>
    </Panel>
  );
}
